/**
 * The predefined roles, read once from the server and shared by Users, Groups
 * and Roles, so no screen carries its own list of role names.
 *
 * The server owns the catalog (`GET /api/admin/roles`, from `iam.role`); this is
 * a cache with a refresh, plus the one navigation Administration needs between
 * its own sections: opening a group from a user's inherited-role chip.
 */

import { Atom } from '../../state/atom.ts';
import { getRoleCatalog } from '../../api/endpoints.ts';
import type { RoleCatalog, RoleSummary } from '../../api/types.ts';

export const roleCatalog = new Atom<RoleCatalog | null>(null);

let inFlight: Promise<RoleCatalog | null> | null = null;

/** The catalog, fetched on first use; `force` refetches (counts change with every assignment). */
export function loadRoleCatalog(force = false): Promise<RoleCatalog | null> {
  if (roleCatalog.value && !force) return Promise.resolve(roleCatalog.value);
  inFlight ??= getRoleCatalog()
    .then((catalog) => {
      roleCatalog.set(catalog);
      return catalog;
    })
    .catch(() => roleCatalog.value)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function rolesOfKind(kind: RoleSummary['kind']): RoleSummary[] {
  return (roleCatalog.value?.roles ?? []).filter((r) => r.kind === kind);
}

export function roleLabel(name: string | null | undefined): string {
  if (!name) return 'None';
  return roleCatalog.value?.roles.find((r) => r.name === name)?.display_name ?? name;
}

/** Permission labels a role grants, in words. */
export function roleCapabilities(name: string): string[] {
  return roleCatalog.value?.roles.find((r) => r.name === name)?.permissions.map((p) => p.label) ?? [];
}

/** Ask Administration to show one group - from a user's inherited-role chip, or a role's assignments. */
export const OPEN_GROUP_EVENT = 'admin-open-group';
let pendingGroup: string | null = null;

export function openAdminGroup(id: string): void {
  pendingGroup = id;
  if (location.hash !== '#groups') location.hash = 'groups';
  window.dispatchEvent(new CustomEvent(OPEN_GROUP_EVENT, { detail: { id } }));
}

/** The group a link asked for and nobody has opened yet. Read once. */
export function takePendingGroup(): string | null {
  const id = pendingGroup;
  pendingGroup = null;
  return id;
}

/** Same for a user, from a role's assignment list. */
export const OPEN_USER_EVENT = 'admin-open-user';
let pendingUser: string | null = null;

export function openAdminUser(id: string): void {
  pendingUser = id;
  if (location.hash !== '#people') location.hash = 'people';
  window.dispatchEvent(new CustomEvent(OPEN_USER_EVENT, { detail: { id } }));
}

export function takePendingUser(): string | null {
  const id = pendingUser;
  pendingUser = null;
  return id;
}
